import React, { createContext, useState, useEffect, useCallback } from "react";
import {
  User,
  UserRole,
  AuthContextType,
  LoginCredentials,
  Permission,
  ROLE_PERMISSIONS,
  rolesDoPerfil,
} from "@models/auth.types";
import { authService } from "@services/authService";
import { perfilService } from "@services/perfilService";

export const AuthContext = createContext<AuthContextType | undefined>(
  undefined
);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  // Carrega o usuário do localStorage ao iniciar
  useEffect(() => {
    const loadUser = () => {
      try {
        const token = authService.getToken();
        const storedUser = localStorage.getItem("user");

        if (token && storedUser) {
          const parsedUser = JSON.parse(storedUser);
          setUser({
            id: parsedUser.usuarioId,
            email: parsedUser.email,
            nome: parsedUser.nome,
            avatarUrl: parsedUser.avatarUrl,
            roles: rolesDoPerfil(parsedUser.perfil),
            createdAt: new Date(),
            lastLogin: new Date(),
          });

          // O perfil guardado é o do último login. Um Admin pode tê-lo trocado
          // depois, então ele é conferido na API a cada abertura do sistema.
          // Se a consulta falhar, vale o guardado — a API recusa de qualquer
          // jeito o que o perfil real não permite.
          perfilService
            .obter()
            .then((p) => {
              const perfil = p.perfil ?? p.role;
              localStorage.setItem(
                "user",
                JSON.stringify({ ...parsedUser, perfil }),
              );
              setUser((atual) =>
                atual ? { ...atual, roles: rolesDoPerfil(perfil) } : atual,
              );
            })
            .catch(() => {});
        }
      } catch (error) {
        console.error("Erro ao carregar usuário:", error);
        authService.logout();
      } finally {
        setLoading(false);
      }
    };

    loadUser();
  }, []);

  // Login tradicional (não será usado - apenas Wrike OAuth)
  const login = useCallback(async (_credentials: LoginCredentials) => {
    setLoading(true);
    try {
      // Login tradicional desabilitado - apenas OAuth Wrike
      throw new Error("Use login com Wrike");
    } catch (error) {
      console.error("Erro no login:", error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  // Login com Wrike OAuth
  const loginWithWrike = useCallback(async () => {
    try {
      // Buscar configuração OAuth do backend
      const config = await authService.getWrikeAuthConfig();

      // Redirecionar para página de autorização do Wrike
      window.location.href = config.authorizationUrl;
    } catch (error) {
      console.error("Erro no login com Wrike:", error);
      throw error;
    }
  }, []);

  // Processa callback do Wrike (chamado por WrikeCallback.tsx)
  const processWrikeCallback = useCallback(async (code: string) => {
    setLoading(true);
    try {
      // Trocar código por tokens
      const response = await authService.exchangeWrikeCode(code);

      // Armazenar dados de autenticação
      authService.setAuthData(response);

      // Atualizar estado do usuário
      setUser({
        id: response.usuarioId,
        email: response.email,
        nome: response.nome,
        avatarUrl: response.avatarUrl,
        roles: rolesDoPerfil(response.perfil),
        createdAt: new Date(),
        lastLogin: new Date(),
      });

      return true;
    } catch (error) {
      console.error("Erro ao processar callback do Wrike:", error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  // Logout
  const logout = useCallback(async () => {
    try {
      await authService.logout();
    } catch (error) {
      console.error("Erro ao fazer logout:", error);
    } finally {
      setUser(null);
    }
  }, []);

  // Verifica se usuário tem role específica
  const hasRole = useCallback(
    (role: UserRole): boolean => {
      return user?.roles.includes(role) ?? false;
    },
    [user]
  );

  // Verifica se usuário tem alguma das roles
  const hasAnyRole = useCallback(
    (roles: UserRole[]): boolean => {
      return roles.some((role) => hasRole(role));
    },
    [hasRole]
  );

  // Verifica se usuário tem permissão específica
  const hasPermission = useCallback(
    (permission: Permission): boolean => {
      if (!user) return false;

      // Admin tem todas as permissões
      if (user.roles.includes(UserRole.ADMIN)) return true;

      // Verifica permissões de cada role do usuário
      return user.roles.some((role) => {
        const rolePermissions = ROLE_PERMISSIONS[role];
        return rolePermissions.includes(permission);
      });
    },
    [user]
  );

  const value: AuthContextType = {
    user,
    loading,
    isAuthenticated: !!user,
    login,
    loginWithWrike,
    logout,
    hasRole,
    hasAnyRole,
    hasPermission,
    processWrikeCallback,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
